import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const FullscreenImageViewer = ({
  products = [],
  currentIndex = null,
  onClose,
  onChangeIndex,
  isLogged = false,
  isHome = false,
  cartItems = [],
  addToCart,
  removeFromCart,
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState("fit"); // "fit" | "panoramic" | "rotate"
  const [isLandscape, setIsLandscape] = useState(false);

  const [flashPrev, setFlashPrev] = useState(false);
  const [flashNext, setFlashNext] = useState(false);

  // Redefinir modo ao trocar de foto
  useEffect(() => {
    setViewMode("fit");
    setIsLandscape(false);
  }, [currentIndex]);

  const handlePrev = (e) => {
    e?.stopPropagation();
    if (currentIndex > 0) {
      setFlashPrev(true);
      setTimeout(() => setFlashPrev(false), 150);
      onChangeIndex(currentIndex - 1);
    }
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    if (currentIndex < products.length - 1) {
      setFlashNext(true);
      setTimeout(() => setFlashNext(false), 150);
      onChangeIndex(currentIndex + 1);
    }
  };

  // Teclado (Esc, Setas) e bloqueio de scroll
  useEffect(() => {
    if (currentIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [currentIndex, products.length, onClose, onChangeIndex]);

  if (currentIndex === null || !products || !products[currentIndex]) {
    return null;
  }

  const activeProduct = products[currentIndex];

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      // É considerada horizontal/panorâmica se a largura for superior à altura
      setIsLandscape(naturalWidth > naturalHeight);
    }
  };

  const getImageUrl = (item) => {
    if (!item) return "";
    if (isHome) {
      return item.image;
    }
    if (!isLogged) {
      return item.imageSample || item.image;
    }
    const isGift = Boolean(item.tags?.toLowerCase().includes("brinde"));
    if (isGift) {
      return item.image;
    }
    return item.imageSample || item.image;
  };

  const isItemInCart =
    Array.isArray(cartItems) &&
    cartItems.some((item) => item._id === activeProduct._id);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col justify-between items-center animate-in fade-in duration-200 select-none"
      onClick={viewMode === "panoramic" ? undefined : onClose}
    >
      {/* Barra Superior */}
      <div
        className="w-full flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-black/90 to-transparent z-10 text-white gap-2 flex-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="font-mono text-xs sm:text-sm font-bold bg-white/10 px-2.5 py-1 rounded-full border border-white/15">
            {t("shop.photoCounter", { current: currentIndex + 1, total: products.length })}
          </span>
          {activeProduct.evento && (
            <span className="text-xs sm:text-sm opacity-80 truncate max-w-[150px] sm:max-w-xs">
              {activeProduct.evento}
            </span>
          )}
        </div>

        {/* Controles para fotos horizontais (celular em pé / panorâmica) */}
        {isLandscape && (
          <div className="flex items-center gap-1 bg-black/60 backdrop-blur-md p-1 rounded-full border border-white/20 shadow-lg order-3 sm:order-2 mx-auto sm:mx-0">
            <button
              type="button"
              onClick={() => setViewMode("fit")}
              title="Ajustar à tela inteira"
              className={`btn btn-xs rounded-full px-2.5 border-none font-medium text-[11px] ${
                viewMode === "fit"
                  ? "bg-blue-500 text-white"
                  : "bg-transparent text-white/70 hover:text-white"
              }`}
            >
              {t("shop.fitMode")}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("panoramic")}
              title="Modo Panorâmico (deslizar horizontalmente)"
              className={`btn btn-xs rounded-full px-2.5 border-none font-medium text-[11px] flex items-center gap-1 ${
                viewMode === "panoramic"
                  ? "bg-blue-500 text-white"
                  : "bg-transparent text-white/70 hover:text-white"
              }`}
            >
              {t("shop.panoramicMode")}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("rotate")}
              title="Girar foto em 90 graus para tela inteira vertical"
              className={`btn btn-xs rounded-full px-2.5 border-none font-medium text-[11px] flex items-center gap-1 ${
                viewMode === "rotate"
                  ? "bg-blue-500 text-white"
                  : "bg-transparent text-white/70 hover:text-white"
              }`}
            >
              <span>🔄</span> Girar 90°
            </button>
          </div>
        )}

        {/* Botão Fechar */}
        <button
          type="button"
          onClick={onClose}
          title="Fechar tela cheia (Esc)"
          aria-label="Fechar tela cheia"
          className="btn btn-circle btn-sm sm:btn-md bg-white/10 hover:bg-white hover:text-black active:bg-white active:text-black text-white border-none transition-colors duration-150 order-2 sm:order-3 ml-auto sm:ml-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 sm:h-6 sm:w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Área Central (Contêiner Estável com Setas Fixas na Tela) */}
      <div className="relative flex-1 w-full flex items-center justify-center overflow-hidden">
        {/* Contêiner da Imagem (com rolagem horizontal isolada em modo panorâmico) */}
        <div
          className={`w-full h-full flex items-center p-2 sm:p-4 ${
            viewMode === "panoramic"
              ? "overflow-x-auto overflow-y-hidden justify-start sm:justify-center scrollbar-thin scrollbar-thumb-white/20 touch-pan-x cursor-grab active:cursor-grabbing"
              : "overflow-hidden justify-center"
          }`}
          onClick={viewMode === "panoramic" ? undefined : onClose}
        >
          <img
            src={getImageUrl(activeProduct)}
            alt={activeProduct.name || activeProduct.evento || "Foto em tela cheia"}
            onLoad={handleImageLoad}
            onClick={(e) => e.stopPropagation()}
            className={`drop-shadow-2xl rounded select-none pointer-events-auto transition-all duration-300 ${
              viewMode === "rotate"
                ? "transform rotate-90 origin-center max-w-[75vh] sm:max-w-[82vh] max-h-[90vw] object-contain"
                : viewMode === "panoramic"
                ? "h-[75vh] sm:h-[82vh] max-w-none w-auto object-contain mx-auto"
                : "max-w-full max-h-[75vh] sm:max-h-[82vh] object-contain"
            }`}
          />

          {/* Dica visual em modo panorâmico */}
          {viewMode === "panoramic" && (
            <div className="fixed bottom-20 left-1/2 -translate-x-1/2 bg-black/80 text-white text-[11px] px-3.5 py-1.5 rounded-full pointer-events-none backdrop-blur-sm border border-white/20 flex items-center gap-1.5 shadow-2xl z-20">
              <span>↔</span>
              <span>Deslize para os lados para explorar toda a foto panorâmica</span>
            </div>
          )}
        </div>

        {/* Botão Foto Anterior (Fixo, sem salto, pisca em branco ao clicar) */}
        {currentIndex > 0 && (
          <button
            type="button"
            onClick={handlePrev}
            title="Foto anterior (Seta esquerda)"
            aria-label="Foto anterior"
            className={`absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center border border-white/20 shadow-2xl cursor-pointer outline-none select-none transition-colors duration-150 ${
              flashPrev
                ? "bg-white text-black"
                : "bg-black/70 text-white hover:bg-black/90 hover:border-white/50"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Botão Próxima Foto (Fixo, sem salto, pisca em branco ao clicar) */}
        {currentIndex < products.length - 1 && (
          <button
            type="button"
            onClick={handleNext}
            title="Próxima foto (Seta direita)"
            aria-label="Próxima foto"
            className={`absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center border border-white/20 shadow-2xl cursor-pointer outline-none select-none transition-colors duration-150 ${
              flashNext
                ? "bg-white text-black"
                : "bg-black/70 text-white hover:bg-black/90 hover:border-white/50"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Barra Inferior com Controles e Ação */}
      <div
        className="w-full flex items-center justify-between p-3 sm:p-4 bg-gradient-to-t from-black/90 to-transparent z-10 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          {activeProduct.atletaNome && (
            <span className="text-xs sm:text-sm bg-white/10 px-3 py-1 rounded-full border border-white/10">
              👤 {activeProduct.atletaNome}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {isHome ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/ar/shop");
              }}
              className="btn bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white btn-sm sm:btn-md gap-2 font-bold shadow-lg border-none"
            >
              {t("home.goToShop")}
            </button>
          ) : isLogged ? (
            <>
              {activeProduct.priceInCents > 0 && (
                <span className="text-base sm:text-xl font-bold">
                  R${" "}
                  {(activeProduct.priceInCents / 100).toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </span>
              )}
              {isItemInCart ? (
                <button
                  type="button"
                  onClick={() => removeFromCart && removeFromCart(activeProduct._id)}
                  className="btn btn-error btn-sm sm:btn-md gap-2 font-bold shadow-lg"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  {t("shop.removeFromCart")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => addToCart && addToCart(activeProduct)}
                  className="btn bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white btn-sm sm:btn-md gap-2 font-bold shadow-lg border-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  {t("shop.wantThisPhoto")}
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/acesso");
              }}
              className="btn bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white btn-sm sm:btn-md gap-2 font-bold shadow-lg border-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {t("shop.buyPhotos")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullscreenImageViewer;
